import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, ArrowTabStepper, Button, Card, useToast,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  ArrowLeft, ArrowRight, ClipboardCheck, Save, Search, X,
} from 'lucide-react';

/**
 * Stock take wizard.
 *
 * 4-step flow that an ops user runs at end-of-day / month-end to
 * reconcile system stock against a physical count:
 *
 *   1. Pick warehouse
 *   2. Scan / count items   (system qty + counted qty side-by-side)
 *   3. Review variances     (only items where counted ≠ system)
 *   4. Post adjustments     (POSTs StockTake header + lines, then per
 *                            non-zero variance writes a StockAdjustment)
 *
 * Designed for a barcode scanner first — the search input is autofocus
 * and pressing Enter on an exact code/barcode adds the line.
 */

interface Warehouse extends Record<string, unknown> {
  warehouseId?: string;
  id?: number | string;
  code?: string;
  name?: string;
}

interface Product extends Record<string, unknown> {
  productId?: string;
  id?: number | string;
  code?: string;
  barcode?: string;
  name?: string;
  costPrice?: number;
  cost?: number;
  isTrackStock?: boolean;
}

interface StockBalance extends Record<string, unknown> {
  productId?: string;
  warehouseId?: string;
  qty?: number;
  quantity?: number;
  quantityOnHand?: number;
}

interface CountLine {
  productId: string | number;
  code: string;
  name: string;
  systemQty: number;
  countedQty: number;
  costPrice: number;
}

const STEPS = [
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'count',     label: 'Count' },
  { key: 'review',    label: 'Review' },
  { key: 'post',      label: 'Post' },
];

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function StockTakeWizard() {
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState(0);
  const [savedIndices, setSavedIndices] = useState<number[]>([]);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [warehouseLabel, setWarehouseLabel] = useState<string>('');
  const [reference, setReference] = useState(`ST-${new Date().toISOString().slice(0, 10)}`);
  const [notes, setNotes] = useState('');

  const [products, setProducts] = useState<Product[]>([]);
  const [stock, setStock] = useState<StockBalance[]>([]);
  const [lines, setLines] = useState<CountLine[]>([]);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Initial load — warehouses, products and current stock.
  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get('/master-data/Warehouse').then(pickList<Warehouse>).catch(() => []),
      apiClient.get('/master-data/Product').then(pickList<Product>).catch(() => []),
      apiClient.get('/purchase/StockBalance').then(pickList<StockBalance>).catch(() => []),
    ])
      .then(([w, p, s]) => {
        setWarehouses(w);
        setProducts(p);
        setStock(s);
        if (w.length === 1) {
          const id = String(w[0].warehouseId ?? w[0].id ?? '');
          setWarehouseId(id);
          setWarehouseLabel(String(w[0].name ?? w[0].code ?? id));
        }
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Load failed'))
      .finally(() => setLoading(false));
  }, []);

  // System qty lookup — keyed by productId for the chosen warehouse.
  const systemQtyByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of stock) {
      // Live StockBalance rows can have a null warehouseId — only apply the
      // warehouse filter when the row actually carries one, else they'd all be
      // dropped and every system-qty would read 0.
      if (warehouseId && s.warehouseId != null && String(s.warehouseId) !== '' && String(s.warehouseId) !== warehouseId) continue;
      const id = String(s.productId ?? '');
      if (!id) continue;
      m.set(id, (m.get(id) ?? 0) + Number(s.quantityOnHand ?? s.qty ?? s.quantity ?? 0));
    }
    return m;
  }, [stock, warehouseId]);

  function addLineForProduct(p: Product, countedQty = 1): void {
    const id = (p.productId ?? p.id) as string | number | undefined;
    if (id == null) return;
    setLines((cur) => {
      const idx = cur.findIndex((l) => String(l.productId) === String(id));
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx]!, countedQty: next[idx]!.countedQty + countedQty };
        return next;
      }
      return [
        ...cur,
        {
          productId: id,
          code: String(p.code ?? p.barcode ?? id),
          name: String(p.name ?? p.code ?? id),
          systemQty: systemQtyByProduct.get(String(id)) ?? 0,
          countedQty,
          costPrice: Number(p.cost ?? p.costPrice ?? 0),
        },
      ];
    });
  }

  function setLineCount(idx: number, countedQty: number) {
    setLines((cur) =>
      cur.map((l, i) => (i === idx ? { ...l, countedQty: Math.max(0, countedQty) } : l)),
    );
  }
  function removeLine(idx: number) {
    setLines((cur) => cur.filter((_, i) => i !== idx));
  }

  // Pressing Enter on an exact match adds the row.
  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    const q = search.trim();
    if (!q) return;
    const exact = products.find(
      (p) =>
        String(p.barcode ?? '').toLowerCase() === q.toLowerCase() ||
        String(p.code ?? '').toLowerCase() === q.toLowerCase(),
    );
    if (exact) {
      addLineForProduct(exact, 1);
      setSearch('');
      e.preventDefault();
    }
  }

  // Filtered search dropdown
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter((p) =>
        [p.name, p.code, p.barcode]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase())
          .some((v) => v.includes(q)),
      )
      .slice(0, 10);
  }, [products, search]);

  const variances = useMemo(
    () =>
      lines
        .map((l) => ({ ...l, delta: l.countedQty - l.systemQty }))
        .filter((l) => l.delta !== 0),
    [lines],
  );

  const summary = useMemo(() => {
    const counted = lines.length;
    const matched = lines.length - variances.length;
    const valueImpact = variances.reduce(
      (s, v) => s + v.delta * v.costPrice,
      0,
    );
    return { counted, matched, varianceCount: variances.length, valueImpact };
  }, [lines, variances]);

  // Step gates
  function next(): void {
    if (step === 0 && !warehouseId) {
      toast.error('Pick a warehouse', 'Choose where the count is happening.');
      return;
    }
    // Entering the Count step: seed the sheet with the FULL product list (with
    // system on-hand pre-filled) so the operator sees everything to count up
    // front — scanning then just increments. Previously the sheet started empty
    // and only grew on scan, so nothing showed until a barcode was read.
    if (step === 0 && lines.length === 0 && products.length > 0) {
      setLines(products
        .filter((p) => p.isTrackStock !== false)
        .map((p) => {
          const id = (p.productId ?? p.id) as string | number;
          return {
            productId: id,
            code: String(p.code ?? p.barcode ?? id),
            name: String(p.name ?? p.code ?? id),
            systemQty: systemQtyByProduct.get(String(id)) ?? 0,
            countedQty: 0,
            costPrice: Number(p.cost ?? p.costPrice ?? 0),
          };
        }));
    }
    if (step === 1 && lines.length === 0) {
      toast.error('Count is empty', 'Scan or add at least one product first.');
      return;
    }
    setSavedIndices((p) => (p.includes(step) ? p : [...p, step]));
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  }
  function back(): void {
    setStep((s) => Math.max(0, s - 1));
  }

  async function postAdjustments(): Promise<void> {
    if (variances.length === 0) {
      toast.error('No variances', 'Counted qty matches system — nothing to post.');
      return;
    }
    setPosting(true);
    try {
      // 1. Header — StockTake.
      try {
        await apiClient.post('/purchase/StockTake', {
          reference,
          warehouseId,
          countedAt: new Date().toISOString(),
          notes: notes || undefined,
          status: 'Posted',
          totalLines: lines.length,
          totalVariances: variances.length,
          valueImpact: round2(summary.valueImpact),
          lines: lines.map((l, i) => ({
            lineNumber: i + 1,
            productId: l.productId,
            systemQty: l.systemQty,
            countedQty: l.countedQty,
            varianceQty: l.countedQty - l.systemQty,
          })),
        });
      } catch (e) {
        console.warn('StockTake post failed:', e);
      }

      // 2. Per-product StockAdjustment for the variances.
      let posted = 0;
      let failed = 0;
      for (const v of variances) {
        try {
          await apiClient.post('/purchase/StockAdjustment', {
            adjustmentDate: new Date().toISOString(),
            warehouseId,
            reference,
            reason: 'Stock take',
            notes: notes || undefined,
            isActive: 1,
            lines: [
              {
                lineNumber: 1,
                productId: v.productId,
                qty: v.delta, // positive = surplus, negative = shortage
                rate: v.costPrice,
                amount: round2(v.delta * v.costPrice),
              },
            ],
          });
          posted++;
        } catch {
          failed++;
        }
      }

      if (failed === 0) {
        toast.success(
          'Stock take posted',
          `${posted} adjustment(s) written. Net value impact ₹${inr(summary.valueImpact)}.`,
        );
      } else {
        toast.error(
          'Partial post',
          `${posted} succeeded · ${failed} failed. Retry from the inventory dashboard.`,
        );
      }
      navigate('/panel/inventory');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/inventory')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Stock take</h1>
          <p className="text-xs text-content-muted">
            {warehouseLabel ? `${warehouseLabel} · ` : ''}
            Reference {reference}
          </p>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card>
        <ArrowTabStepper
          compact
          steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
          activeIndex={step}
          savedIndices={savedIndices}
          onChange={(i) => setStep(i)}
        />

        <div className="mt-4">
          {step === 0 && (
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">
                  Warehouse <span className="text-danger">*</span>
                </span>
                <select
                  className={inputCls}
                  value={warehouseId}
                  onChange={(e) => {
                    setWarehouseId(e.target.value);
                    const w = warehouses.find(
                      (x) => String(x.warehouseId ?? x.id) === e.target.value,
                    );
                    setWarehouseLabel(String(w?.name ?? w?.code ?? ''));
                  }}
                >
                  <option value="">— pick a warehouse —</option>
                  {warehouses.map((w) => {
                    const id = String(w.warehouseId ?? w.id ?? '');
                    return (
                      <option key={id} value={id}>
                        {w.name ?? w.code ?? id}
                      </option>
                    );
                  })}
                </select>
                {loading && <span className="text-[11px] text-content-muted">Loading warehouses…</span>}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Reference</span>
                <input
                  className={inputCls}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs font-medium text-content-secondary">Notes</span>
                <input
                  className={inputCls}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason / observer / any context for the audit log"
                />
              </label>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  ref={searchRef}
                  autoFocus
                  className={`${inputCls} pl-9`}
                  placeholder="Scan barcode or search by name / code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={onSearchKey}
                />
                {filtered.length > 0 && search && (
                  <div className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-surface-card shadow-lg">
                    {filtered.map((p) => {
                      const id = String(p.productId ?? p.id ?? '');
                      return (
                        <button
                          key={id}
                          type="button"
                          className="flex w-full items-start justify-between gap-3 border-b border-border/40 px-3 py-2 text-left text-sm hover:bg-surface-card-hover"
                          onClick={() => {
                            addLineForProduct(p, 1);
                            setSearch('');
                            searchRef.current?.focus();
                          }}
                        >
                          <div>
                            <div className="font-medium">{p.name ?? p.code}</div>
                            <div className="text-[11px] text-content-muted">
                              {p.code} · system qty {systemQtyByProduct.get(id) ?? 0}
                            </div>
                          </div>
                          <span className="text-[11px] text-primary">Add</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="text-xs text-content-muted">
                    <tr className="border-b border-border bg-surface-card">
                      <th className="px-2 py-2 text-left">Product</th>
                      <th className="px-2 py-2 text-right">System</th>
                      <th className="px-2 py-2 text-right">Counted</th>
                      <th className="px-2 py-2 text-right">Δ</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-2 py-6 text-center text-xs text-content-muted">
                          Scan a barcode or search to add the first item.
                        </td>
                      </tr>
                    ) : (
                      lines.map((l, i) => {
                        const delta = l.countedQty - l.systemQty;
                        return (
                          <tr key={String(l.productId)} className="border-b border-border/40">
                            <td className="px-2 py-2">
                              <div className="font-medium">{l.name}</div>
                              <div className="text-[11px] text-content-muted">{l.code}</div>
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">{l.systemQty}</td>
                            <td className="px-2 py-2 text-right">
                              <input
                                type="number"
                                className={`${inputCls} text-right`}
                                value={l.countedQty}
                                min={0}
                                step={1}
                                onChange={(e) => setLineCount(i, Number(e.target.value))}
                                style={{ width: 80 }}
                              />
                            </td>
                            <td
                              className={`px-2 py-2 text-right tabular-nums ${
                                delta === 0
                                  ? 'text-content-muted'
                                  : delta > 0
                                    ? 'text-success'
                                    : 'text-danger'
                              }`}
                            >
                              {delta > 0 ? `+${delta}` : delta}
                            </td>
                            <td className="px-2 py-2">
                              <button
                                type="button"
                                className="text-content-muted hover:text-danger"
                                onClick={() => removeLine(i)}
                                aria-label="Remove"
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-content-muted">
                {lines.length} line(s) counted · {variances.length} variance(s) so far
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <SummaryStat label="Lines counted" value={summary.counted} />
                <SummaryStat label="Variances" value={summary.varianceCount} cls={summary.varianceCount ? 'text-warning' : 'text-success'} />
                <SummaryStat
                  label="Net value impact"
                  value={`₹ ${inr(summary.valueImpact)}`}
                  cls={summary.valueImpact === 0 ? 'text-content-muted' : summary.valueImpact > 0 ? 'text-success' : 'text-danger'}
                />
              </div>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="text-xs text-content-muted">
                    <tr className="border-b border-border bg-surface-card">
                      <th className="px-2 py-2 text-left">Product</th>
                      <th className="px-2 py-2 text-right">System</th>
                      <th className="px-2 py-2 text-right">Counted</th>
                      <th className="px-2 py-2 text-right">Δ</th>
                      <th className="px-2 py-2 text-right">Value impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variances.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-2 py-6 text-center text-xs text-success">
                          ✓ All counted lines match the system. Nothing to adjust.
                        </td>
                      </tr>
                    ) : (
                      variances.map((v) => (
                        <tr key={String(v.productId)} className="border-b border-border/40">
                          <td className="px-2 py-2">
                            <div className="font-medium">{v.name}</div>
                            <div className="text-[11px] text-content-muted">{v.code}</div>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">{v.systemQty}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{v.countedQty}</td>
                          <td
                            className={`px-2 py-2 text-right tabular-nums ${
                              v.delta > 0 ? 'text-success' : 'text-danger'
                            }`}
                          >
                            {v.delta > 0 ? `+${v.delta}` : v.delta}
                          </td>
                          <td
                            className={`px-2 py-2 text-right tabular-nums ${
                              v.delta * v.costPrice > 0 ? 'text-success' : 'text-danger'
                            }`}
                          >
                            ₹ {inr(v.delta * v.costPrice)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <Alert variant={variances.length === 0 ? 'success' : 'warning'}>
                {variances.length === 0
                  ? '✓ No variances — clicking Post will record the take with zero adjustments.'
                  : `Posting will write 1 StockTake header + ${variances.length} StockAdjustment row(s). Net value impact ₹${inr(summary.valueImpact)}.`}
              </Alert>
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                <SummaryStat label="Warehouse" value={warehouseLabel || '—'} />
                <SummaryStat label="Reference" value={reference} />
                <SummaryStat label="Lines counted" value={summary.counted} />
                <SummaryStat label="Variances" value={summary.varianceCount} cls={summary.varianceCount ? 'text-warning' : 'text-success'} />
              </div>
              {notes && (
                <div className="rounded-md border border-border bg-surface-card px-3 py-2 text-xs">
                  <span className="text-content-muted">Notes: </span>
                  <span className="text-content-primary">{notes}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
          <div className="text-xs text-content-muted">
            Step {step + 1} of {STEPS.length} — {STEPS[step]?.label}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/panel/inventory')} disabled={posting}>
              Cancel
            </Button>
            {step > 0 && (
              <Button variant="outline" onClick={back} disabled={posting}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button onClick={next} disabled={posting}>
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            )}
            {step === STEPS.length - 1 && (
              <Button onClick={postAdjustments} disabled={posting}>
                {variances.length === 0 ? <Save className="h-4 w-4" /> : <ClipboardCheck className="h-4 w-4" />}
                {posting ? 'Posting…' : 'Post stock take'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function SummaryStat({ label, value, cls }: { label: string; value: string | number; cls?: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-card px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-content-muted">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${cls ?? 'text-content-primary'}`}>{value}</div>
    </div>
  );
}

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
